import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { AnalyticsCharts } from "./AnalyticsOverview";
import { buildPersonaPortfolioLanding, resolvePersona, workspaceLabel } from "../lib/personas";
import { fillVoice } from "../lib/master";

/** Persona-driven portfolio landing — analytics first, not forms. */
export default function PersonaLanding({ portfolio, guide, user, onStartTour }) {
  const navigate = useNavigate();
  if (!portfolio) return null;

  const access = user?.access;
  const persona = resolvePersona(guide, access);
  const landing = buildPersonaPortfolioLanding(portfolio, guide);
  const count = portfolio.applications?.length || 0;
  const headline = fillVoice(guide.headline, count);
  const wsLabel = workspaceLabel(persona.workspace || access?.workspace);

  return (
    <section className="persona-landing" data-testid="persona-landing" data-persona={persona.id}>
      <header className="analytics-landing-head persona-landing-head" data-testid="role-home">
        <p className="kicker">{wsLabel} · {persona.label}</p>
        <h1 data-testid="role-home-headline">{headline}</h1>
        <p className="persona-main-question" data-testid="persona-main-question">{landing.mainQuestion}</p>
        {access?.may_approve?.length > 0 && (
          <p className="muted small-note" data-testid="persona-decision-authority">
            Decision authority: {access.may_approve.join(", ")} · Role alone does not grant all approvals.
          </p>
        )}
        <p className="analytics-landing-meta muted" data-testid="analytics-context-line">
          {portfolio.tenant?.name} · {portfolio.tenant?.project || "Programme"} · {count} applications
          {guide.accountable_owner ? ` · ${guide.accountable_owner}` : ""}
        </p>
      </header>

      <div className="persona-kpi-grid metric-grid" data-testid="persona-kpi-grid">
        {landing.tiles.map((tile) => {
          const inner = (
            <>
              <div>
                <small>{tile.label}</small>
                <strong>{tile.value}</strong>
                <span>{tile.detail}</span>
              </div>
              {tile.href && <ChevronRight size={16} className="metric-chevron" />}
            </>
          );
          return tile.href ? (
            <Link key={tile.id} to={tile.href} className={`metric metric-drill tone-${tile.tone}`} data-testid={`persona-kpi-${tile.id}`}>
              {inner}
            </Link>
          ) : (
            <div key={tile.id} className={`metric tone-${tile.tone}`} data-testid={`persona-kpi-${tile.id}`}>
              {inner}
            </div>
          );
        })}
      </div>

      {landing.nextAction && (
        <article className="feature-panel persona-next-action" data-testid="persona-next-action">
          <p className="kicker">RECOMMENDED NEXT ACTION</p>
          <strong>{landing.nextAction.label}</strong>
          <p className="muted small-note">{landing.nextAction.detail}</p>
          {landing.nextAction.route && (
            <button
              type="button"
              className="primary-button"
              data-testid="persona-next-action-btn"
              onClick={() => navigate(landing.nextAction.route.startsWith("/") ? landing.nextAction.route : `/${landing.nextAction.route}`)}
            >
              {landing.nextAction.label} <ArrowUpRight size={15} />
            </button>
          )}
        </article>
      )}

      <div className="persona-charts-section" data-testid="persona-charts-section">
        <p className="kicker">DRILL-DOWN · CHARTS</p>
        <AnalyticsCharts portfolio={portfolio} guide={guide} />
      </div>

      {persona.forbiddenActions?.length > 0 && (
        <p className="muted small-note persona-boundary" data-testid="persona-boundary">
          This persona cannot: {persona.forbiddenActions.slice(0, 3).join(", ").replaceAll("_", " ")}.
        </p>
      )}
    </section>
  );
}
